/* eslint-disable import/no-relative-packages, import/no-extraneous-dependencies */
import {Action, configureStore, StateFromReducersMapObject, ThunkAction} from '@reduxjs/toolkit';
import {AuthorizationFetcher, LocaleUtils} from '@wallaby-ui/core';
import {csCoreMessages, deCoreMessages, enCoreMessages, plCoreMessages, skCoreMessages} from '@wallaby-ui/layout';
import {createBrowserHistory} from 'history';
import {combineReducers} from 'redux';
import {createReduxHistoryContext} from 'redux-first-history';

import i18nConfig from './config/common/i18n_config.json';
import navigationConfig from './config/common/navigation_config';
import userSettingsConfig from './config/common/user_settings_config.json';
import infectionByOriginChartConfig from './config/feature/dashboard/infection_by_origin_chart_config.json';
import infectionByTrendChartConfig from './config/feature/dashboard/infection_by_trend_chart_config.json';
import infectionByTypeChartConfig from './config/feature/dashboard/infection_by_type_chart_config.json';
import haiWipTableGridConfig from './config/feature/dashboard/most_recent_hai_grid_config.json';
import crpInTimeChartConfig from './config/feature/encounter_detail/crp_in_time_chart_config.json';
import documentsTableConfig from './config/feature/encounter_detail/documents_table_grid_config.json';
import pctInTimeChartConfig from './config/feature/encounter_detail/pct_in_time_chart_config.json';
import encounterTimelineTableConfig from './config/feature/encounter_detail/timeline_table_grid_config.json';
import wbcInTimeChartConfig from './config/feature/encounter_detail/wbc_in_time_chart_config.json';
import haisFiltersConfig from './config/feature/hai_list/filters_config.json';
import haiListGridConfig from './config/feature/hai_list/hai_list_grid_config.json';
import healthcareTableGridConfig from './config/feature/patient_detail/patient_detail_grid_config.json';
import patientFiltersConfig from './config/feature/patient_list/filters_config.json';
import patientListGridConfig from './config/feature/patient_list/patient_list_grid_config.json';
import departmentFiltersConfig from './config/feature/reports/department_filters_config.json';
import departmentReportConfig from './config/feature/reports/department_report_config.json';
import hospitalFiltersConfig from './config/feature/reports/hospital_filters_config.json';
import hospitalReportConfig from './config/feature/reports/hospital_report_config.json';
import csMessages from './config/locale/cs.json';
import deMessages from './config/locale/de.json';
import enMessages from './config/locale/en.json';
import plMessages from './config/locale/pl.json';
import skMessages from './config/locale/sk.json';
import createReducer from './reducers';

const thunkConfig = {
  extraArgument: {
    config: {
      common: {
        navigation: navigationConfig,
        i18n: i18nConfig,
        userSettings: userSettingsConfig,
      },
      feature: {
        dashboard: {
          haiByType: infectionByTypeChartConfig,
          haiByOrganization: infectionByOriginChartConfig,
          haiByTrend: infectionByTrendChartConfig,
          haiTable: haiWipTableGridConfig,
        },
        patient: {
          detail: {
            healthcareTable: healthcareTableGridConfig,
          },
          list: {
            filters: patientFiltersConfig,
            grid: patientListGridConfig,
          },
        },
        hai: {
          list: {
            grid: haiListGridConfig,
            filters: haisFiltersConfig,
          },
        },
        encounter: {
          detail: {
            documentsTable: documentsTableConfig,
            cprInTimeChart: crpInTimeChartConfig,
            wbcInTimeChart: wbcInTimeChartConfig,
            pctInTimeChart: pctInTimeChartConfig,
            timelineTable: encounterTimelineTableConfig,
          },
        },
        reports: {
          department: {
            report: departmentReportConfig,
            filters: departmentFiltersConfig,
          },
          hospital: {
            report: hospitalReportConfig,
            filters: hospitalFiltersConfig,
          },
        },
      },
      locales: {
        core: {
          cs: csCoreMessages,
          de: deCoreMessages,
          en: enCoreMessages,
          sk: skCoreMessages,
          pl: plCoreMessages,
        },
        application: {
          cs: csMessages,
          de: deMessages,
          en: enMessages,
          sk: skMessages,
          pl: plMessages,
        },
      },
    },
  },
};

const initializeStore = () => {
  const {createReduxHistory, routerMiddleware, routerReducer} = createReduxHistoryContext({
    history: createBrowserHistory(),
  });

  const store = configureStore({
    reducer: combineReducers(createReducer(routerReducer)),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: (() => {
          thunkConfig.extraArgument.config.common.i18n = LocaleUtils.detectAndUpdateConfigPreferredLanguage(
            i18nConfig,
            i18nConfig.config.usedLanguages,
            (language, config) => {
              // eslint-disable-next-line no-param-reassign
              config.config.preferredLanguage = language;
            },
            i18nConfig.config.preferredLanguage
          );
          return thunkConfig;
        })(),
        // TODO: turned off serializable checks for now as old code contains multiple cases of storing non-serializable data
        serializableCheck: false,
      }).concat(routerMiddleware),
  });

  store.asyncReducers = {};
  store.injectReducer = (key, reducer) => {
    store.asyncReducers[key] = reducer;
    store.replaceReducer(combineReducers(createReducer(routerReducer, store.asyncReducers)));
    return store;
  };

  // init fetcher interceptor
  AuthorizationFetcher.initStore(store);

  const history = createReduxHistory(store);

  return {store, history};
};

export type AppStore = ReturnType<typeof initializeStore>['store'];

export type AppDispatch = AppStore['dispatch'];

export type RootState = StateFromReducersMapObject<ReturnType<typeof createReducer>>;

type ExtraArgument = typeof thunkConfig.extraArgument;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, ExtraArgument, Action<string>>;

export default initializeStore;
